// packages/shared/components/Security/TwoFactorEnableScreen.web.jsx
import React, { useState, useEffect } from 'react';
import { useTwoFactorSetup } from '@mysimpledb/shared/hooks/2fa/useTwoFactorSetup';
import { toast } from 'react-hot-toast';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import api from '@mysimpledb/shared/config/axios';

export default function TwoFactorEnableScreen() {
  const {
    qrDataUrl,
    code,
    setCode,
    recoveryCodes,
    loading,
    error,
    canSubmit,
    verify,
  } = useTwoFactorSetup();

  const [enabled, setEnabled] = useState(false);
  const [loadingMe, setLoadingMe] = useState(true);
  const [disabling, setDisabling] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [password, setPassword] = useState('');

  function openDisable() {
    setPassword('');
    setShowDisable(true);
  }

  function toMsg(e) {
    return e?.response?.data?.error ?? e?.message ?? String(e);
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/me'); // returns { twofa_enabled, ... }
        setEnabled(!!r.data.twofa_enabled);
      } catch (e) {
        toast.error(toMsg(e));
      } finally {
        setLoadingMe(false);
      }
    })();
  }, []);

  async function confirmDisable(e) {
    e?.preventDefault();
    if (!password) return;
    setDisabling(true);
    try {
      await api.post('/twofa/disable', { password }); // ← your existing route
      toast.success('Two-Factor Authentication disabled.');
      setEnabled(false);
      setShowDisable(false);
    } catch (e) {
      toast.error(e?.response?.data?.error ?? e?.message ?? String(e));
    } finally {
      setDisabling(false);
    }
  }

  if (loadingMe) {
    return (
      <div
        style={{
          maxWidth: 640,
          margin: '24px auto',
          background: '#fff',
          borderRadius: 16,
          padding: 16,
          border: '1px solid rgba(0,0,0,.06)',
          boxShadow: '0 8px 24px rgba(0,0,0,.06)',
        }}
      >
        Loading security status…
      </div>
    );
  }

  // --- If already enabled: show only the Disable card ---
  if (enabled) {
    return (
      <>
        <div
          style={{
            maxWidth: 640,
            margin: '24px auto',
            background: '#fff',
            borderRadius: 16,
            padding: 16,
            border: '1px solid rgba(0,0,0,.06)',
            boxShadow: '0 8px 24px rgba(0,0,0,.06)',
          }}
        >
          <h2 style={{ marginTop: 0 }}>Two-Factor Authentication</h2>
          <div
            style={{
              padding: 12,
              borderRadius: 12,
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              marginBottom: 16,
            }}
          >
            <strong>Status:</strong> On
          </div>
          <p>
            Your account is protected with an authenticator app (TOTP). To turn
            this off, confirm with your password.
          </p>
          <AppButton
            variant="danger"
            size="sm"
            onPress={openDisable}
            style={{ width: 300 }}
          >
            {disabling ? 'Disabling…' : 'Disable Two-Factor Authentication'}
          </AppButton>
        </div>
        {showDisable && (
          <div
            style={backdropStyle}
            onClick={() => !disabling && setShowDisable(false)}
          >
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>Confirm disable</h3>
              <p>Enter your account password to disable 2FA.</p>
              <form onSubmit={confirmDisable}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoFocus
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="submit"
                    disabled={!password || disabling}
                    style={primaryBtn}
                  >
                    {disabling ? 'Disabling…' : 'Disable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDisable(false)}
                    disabled={disabling}
                    style={ghostBtn}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }

  // --- Not enabled: show existing enable flow ---
  return (
    <div
      style={{
        maxWidth: 640,
        margin: '24px auto',
        background: '#fff',
        borderRadius: 16,
        padding: 16,
        border: '1px solid rgba(0,0,0,.06)',
        boxShadow: '0 8px 24px rgba(0,0,0,.06)',
      }}
    >
      {recoveryCodes.length === 0 ? (
        <>
          <h2 style={{ marginTop: 0 }}>Enable Two-Factor Authentication</h2>
          <p>
            Scan this QR with your authenticator app, then enter the 6-digit
            code.
          </p>

          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="2FA QR"
              style={{
                width: 220,
                height: 220,
                display: 'block',
                margin: '16px auto',
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              Loading…
            </div>
          )}

          {!!error && (
            <div style={{ color: '#b00020', marginBottom: 8 }}>{error}</div>
          )}

          <input
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{
              width: 180,
              padding: 10,
              borderRadius: 8,
              border: '1px solid #ccc',
              marginRight: 8,
              fontSize: 18,
              letterSpacing: 2,
              textAlign: 'center',
            }}
          />
          <AppButton onPress={verify} disabled={!canSubmit || loading}>
            {loading ? 'Verifying…' : 'Verify & Enable'}
          </AppButton>
        </>
      ) : (
        <>
          <h3>Recovery Codes (save these now)</h3>
          <p>
            Store these in a safe place. Each code can be used once if you lose
            access to your device.
          </p>
          <ul
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              background: '#f9fafb',
              padding: 12,
              borderRadius: 12,
            }}
          >
            {recoveryCodes.map((c, i) => (
              <li key={i} style={{ margin: '4px 0' }}>
                {c}
              </li>
            ))}
          </ul>
          <button onClick={() => window.history.back()}>I saved these</button>
        </>
      )}
    </div>
  );
}

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};
const modalStyle = {
  width: 'min(420px, 92vw)',
  background: '#fff',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,.2)',
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
};
const primaryBtn = {
  background: '#ef4444',
  color: '#fff',
  border: 0,
  padding: '10px 14px',
  borderRadius: 8,
  cursor: 'pointer',
};
const ghostBtn = {
  background: 'transparent',
  border: '1px solid #d1d5db',
  padding: '10px 14px',
  borderRadius: 8,
  cursor: 'pointer',
};
